import type { FC } from 'react';
import { ButtonAnt } from '@/components/buttons/button/ButtonAnt';
import { Form, Input } from '@/components/form';
import { FormattedMessage, useIntl } from '@/components/formatted_message/FormattedMessage';
import { Flex } from '@/components/layouting';
import { Link } from '@/components/link';
import { TypographySize } from '@/components/typography/enums/size';
import { useAuthContext } from '@/contexts/AuthContext';
import { StyledFormWrapper } from '@/routes/auth/components/FormContainer';
import { commonMessages } from '../../../messages';
import { Form as messages } from '../messages';
import { Icon } from '@/components/icon';

interface LoginFormValues {
  email: string;
  password: string;
}

export const LoginForm: FC = () => {
  const intl = useIntl();
  const [form] = Form.useForm<LoginFormValues>();
  const { login, loading, resetMutation, identifiedUser } = useAuthContext();

  const handleSubmit = ({ email, password }: LoginFormValues) => {
    login({ email, password });
  };

  const LOGIN_FORM_INITIAL_VALUES = {
    email: identifiedUser.identifier,
    password: '',
    staySigned: true,
  };

  return (
    <StyledFormWrapper>
      <Form
        form={form}
        initialValues={LOGIN_FORM_INITIAL_VALUES}
        layout="vertical"
        onChangeCapture={() => resetMutation.login()}
        onFinish={handleSubmit}
      >
        <Flex vertical gap={8}>
          <Form.Item
            label={intl.formatMessage(messages.email)}
            name="email"
            rules={[
              {
                required: true,
                message: intl.formatMessage(commonMessages.emailRequired),
                validateTrigger: 'onSubmit',
              },
              {
                type: 'email',
                message: intl.formatMessage(commonMessages.emailNotValid),
                validateTrigger: 'onSubmit',
              },
            ]}
          >
            <Input data-cy="login-email" data-test="login-email" placeholder={intl.formatMessage(messages.email)} />
          </Form.Item>
          <Form.Item
            label={intl.formatMessage(messages.password)}
            name="password"
            rules={[
              {
                required: true,
                message: intl.formatMessage(messages.passwordRequired),
                validateTrigger: 'onSubmit',
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <Input.Password
              autoFocus
              data-cy="login-password"
              data-test="login-password"
              iconRender={visible => <Icon iconString={visible ? 'eye' : 'eyeSlash'} style={{ cursor: 'pointer' }} />}
              placeholder={intl.formatMessage(messages.password)}
            />
          </Form.Item>
        </Flex>

        <Flex align="normal" justify="flex-end" style={{ padding: '8px 0px 36px 0px', marginTop: '0px' }}>
          <Link
            data-cy="login-forgot-password"
            onClick={() => resetMutation.login()}
            size={TypographySize.Small}
            to="/forgot-password"
          >
            <FormattedMessage {...messages.forgotPassword} />
          </Link>
        </Flex>

        <ButtonAnt
          data-cy="login-submit-button"
          data-test="login-submit-button"
          htmlType="submit"
          loading={loading.isLoadingLogin}
          size="large"
          type="primary"
        >
          <FormattedMessage {...messages.login} />
        </ButtonAnt>
      </Form>
    </StyledFormWrapper>
  );
};
